import { Button } from '@/global/components/Button'
import CalendarioCompetencia from '@/global/components/CalendarioCompetencia'
import type { EventoCalendario } from '@/global/components/CalendarioMensal'
import { diasDaGrade } from '@/global/components/calendario-mensal.utils'
import { useThemeStore } from '@/global/components/ToggleTheme'
import dayjs, { type Dayjs } from 'dayjs'
import { PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { TabelaHorarioFuncionario } from 'service/api'
import { diasComHorario } from '../horario-do-dia'
import { generateColor } from '../utils'
import { AdicionarTabelaHorarioFuncionario } from './modals/Adicionar'
import { EditarTabelaHorarioFuncionario } from './modals/ExcluirEditar'
import { HorarioDoDia } from './modals/HorarioDoDia'

interface IProps {
    /** `YYYY-MM` exibido. */
    value: string
    onChange: (competencia: string) => void
    tabelas?: TabelaHorarioFuncionario[]
    editable?: boolean
}

export default function CalendarioTabelasHorario({
    value: competencia,
    onChange,
    tabelas = [],
    editable = true,
}: IProps) {
    const [open, setOpen] = useState(false)
    const [periodo, setPeriodo] = useState<{
        dtInicial: string
        dtFinal: string | null
    } | null>(null)
    const [tabelaEditar, setTabelaEditar] =
        useState<TabelaHorarioFuncionario | null>(null)
    const [horarioAberto, setHorarioAberto] = useState<{
        tabela: TabelaHorarioFuncionario
        dia: Dayjs
    } | null>(null)
    const mode = useThemeStore(state => state.theme)

    function eventoDaTabela(
        tabela: TabelaHorarioFuncionario,
        id: string,
        inicio: string,
        fim: string | null,
    ): EventoCalendario {
        const { nome } = tabela.TabelaHorario
        const { light, dark } = generateColor(
            `${tabela.tabelaHorarioId}-${nome}`,
        )
        return {
            id,
            titulo: nome,
            inicio,
            fim,
            cor: mode === 'dark' ? dark : light,
            corTexto: mode === 'dark' ? 'white' : 'black',
            className: tabela.isTrocaAutomatica ? 'italic' : undefined,
            dica:
                nome + (tabela.isTrocaAutomatica ? ' (Troca automática)' : ''),
        }
    }

    // edição mostra o período inteiro; leitura, só os dias com horário
    const eventos: EventoCalendario[] = editable
        ? tabelas.map(it =>
              eventoDaTabela(
                  it,
                  it.id.toString(),
                  dayjs.utc(it.dtInicial).format('YYYY-MM-DD'),
                  it.dtFinal
                      ? dayjs.utc(it.dtFinal).format('YYYY-MM-DD')
                      : null,
              ),
          )
        : diasComHorario(tabelas, diasDaGrade(competencia)).map(
              ({ dia, tabela }) => {
                  const data = dia.format('YYYY-MM-DD')
                  return eventoDaTabela(
                      tabela,
                      `${tabela.id}|${data}`,
                      data,
                      data,
                  )
              },
          )

    return [
        <CalendarioCompetencia
            key="calendario"
            competencia={competencia}
            onCompetenciaChange={onChange}
            acoes={
                editable && (
                    <Button
                        onClick={() => {
                            setPeriodo(null)
                            setOpen(true)
                        }}
                        title="Adicionar"
                        className="size-10"
                    >
                        <PlusIcon className="size-8 stroke-3" />
                    </Button>
                )
            }
            eventos={eventos}
            rotuloOcultos={quantidade => `+${quantidade} ignorado(s)`}
            onEventoClick={(id, dia) => {
                const tabelaId = editable ? id : id.split('|')[0]
                const tabela =
                    tabelas.find(it => it.id.toString() === tabelaId) ?? null
                if (editable) setTabelaEditar(tabela)
                else if (tabela) setHorarioAberto({ tabela, dia })
            }}
            permiteArraste
            onDiaAdd={
                editable
                    ? (inicio, fim) => {
                          setPeriodo({
                              dtInicial: inicio.format('YYYY-MM-DD'),
                              dtFinal: fim?.format('YYYY-MM-DD') ?? null,
                          })
                          setOpen(true)
                      }
                    : undefined
            }
        />,
        <EditarTabelaHorarioFuncionario
            open={!!tabelaEditar}
            onClose={() => {
                setTabelaEditar(null)
            }}
            tabela={tabelaEditar!}
            disableFields={!editable}
        />,
        <HorarioDoDia
            aberto={horarioAberto}
            onClose={() => setHorarioAberto(null)}
        />,
        <AdicionarTabelaHorarioFuncionario
            // remonta a cada período: os valores iniciais do form valem uma vez
            key={
                periodo ? `${periodo.dtInicial}-${periodo.dtFinal}` : 'sem-data'
            }
            onClose={() => setOpen(false)}
            open={open}
            dtInicial={periodo?.dtInicial}
            dtFinal={periodo?.dtFinal}
        />,
    ]
}
